// Shared transient toast. Consolidates the bottom-right Kendo NotificationGroup
// + Fade + Notification block that every page hand-rolled, so a toast looks and
// behaves the same everywhere:
//   * fixed bottom-right position,
//   * one consistent font size,
//   * auto-dismiss after 10s (manual ✕ still works).
//
// Pages keep owning their toast state (a `useState`) and render one <ToastHost>
// with it — the value can be a plain message string or a `{ kind, text }` object,
// so existing call sites drop in with minimal change.
//
// A toast may also carry an `action` (a button rendered beside the text) and opt
// out of auto-dismiss with `autoDismissMs={null}` — together those cover the
// "here is a thing to do about it" toast, like the service-worker update prompt,
// which previously hand-rolled its own Notification and so missed the font size.

import { useEffect, useRef, type ReactNode } from 'react';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

/** A page's toast value: a plain message, or a message with an explicit kind. */
export type ToastValue = string | { kind?: ToastKind; text: string };

/** How long a toast stays on screen before auto-dismissing. */
export const TOAST_AUTO_DISMISS_MS = 10_000;

/** One consistent toast text size across the app — the app's 14px base, so
 *  toasts read at normal page size (Kendo's default notification text is smaller). */
const TOAST_FONT_SIZE = 14;

export interface ToastHostProps {
  /** Current toast, or null when nothing is shown. */
  toast: ToastValue | null;
  /** Kind for a string toast (or an object toast that omits its own kind). Defaults to 'info'. */
  kind?: ToastKind;
  /** Clear the toast — called by the ✕ button and by the auto-dismiss timer. */
  onClose: () => void;
  /** Override the auto-dismiss window; defaults to 10s. `null` keeps the toast
   *  up until it is dismissed — for a toast whose action the user must see. */
  autoDismissMs?: number | null;
  /** Optional control rendered beside the text, e.g. a Reload button. A toast
   *  with an action normally wants `autoDismissMs={null}` too. */
  action?: ReactNode;
}

export function ToastHost({
  toast,
  kind = 'info',
  onClose,
  autoDismissMs = TOAST_AUTO_DISMISS_MS,
  action,
}: ToastHostProps) {
  // Keep onClose in a ref so the timer effect only re-arms when the shown toast
  // (or the window) changes — not on every parent render, which would otherwise
  // keep resetting the timer and the toast would never auto-dismiss.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (toast == null || autoDismissMs == null) return;
    const timer = setTimeout(() => onCloseRef.current(), autoDismissMs);
    return () => clearTimeout(timer);
  }, [toast, autoDismissMs]);

  const text = typeof toast === 'string' ? toast : toast?.text;
  const effectiveKind: ToastKind = (typeof toast === 'object' && toast?.kind) || kind;

  return (
    <NotificationGroup style={{ position: 'fixed', right: 16, bottom: 16, alignItems: 'flex-end' }}>
      <Fade>
        {toast != null && (
          <Notification type={{ style: effectiveKind, icon: true }} closable onClose={onClose}>
            {action ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: TOAST_FONT_SIZE }}>{text}</span>
                {action}
              </div>
            ) : (
              <span style={{ fontSize: TOAST_FONT_SIZE }}>{text}</span>
            )}
          </Notification>
        )}
      </Fade>
    </NotificationGroup>
  );
}

export default ToastHost;
